// Global
import React, { useMemo } from 'react';

// Local
import { Hero } from './Hero';
import { Data } from '@generated/Foundation.GMI.model';
import { PageTypes } from '@generated/Project.GMI.model';
import { HeroCardAlignment, HeroType } from 'lib/services/models/types';
import { isTemplate } from 'lib/utils/sitecore/data-template';
import { usePlainTextWrapper } from 'components/helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { useLinkWrapper } from 'components/helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { useRichTextWrapper } from 'components/helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { useImageWrapper } from 'components/helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import styles from './Hero.module.scss';
import { ComponentParams } from '@sitecore-content-sdk/nextjs';
import { Themes } from 'lib/themes';
import { Content } from '@generated/Content/Hero.model';
import { CtaComponentClass } from 'lib/hooks/theming/useCtaComponentClass';
import { useSitecorePage } from 'lib/hooks/sitecore/useSitecorePage';
import useIsEditing from 'lib/hooks/sitecore/useIsEditing';
import { EditingHelpText } from 'components/helpers/Editing/EditingHelpText';
import { getActionToolbarButtons } from 'components/helpers/ActionToolbar/Utils/ActionToolbar.utils';
import { useSiteSettings } from 'lib/hooks/useSiteSettings';
import { useSitecoreContext } from 'lib/hooks/useSitecoreContext';
type HeroProps = Omit<Content.Hero.RenderingParameters.HeroParameters_Component, 'params'> & {
  params: ComponentParams & {
    FieldNames: HeroType;
    Styles: string;
  };
};

type ArticleSettings = Data.Settings.ArticleSettings & {
  fields?: {
    defaultAuthor?: Data.Contributor.AuthorContributor_Item;
    heroActionToolBarButtons?: Data.Content.ActionToolBarButton[];
    heroEventPosition?: { value?: string };
    hideAuthorBySignaturePrefix?: { value?: boolean };
  };
};

const HeroDynamic = (props: HeroProps) => {
  const route = useSitecorePage();
  const siteSettings = useSiteSettings();
  const isEditing = useIsEditing();
  const sitecoreContext = useSitecoreContext();

  const disableHeroActionToolbar = route?.fields?.disableHeroActionToolbar?.value;

  // Check if current page is an article (GeneralLandingPage) for mobile alignment
  const isArticlePage = route
    ? isTemplate(route, PageTypes.Constants.GeneralLandingPage.TemplateId)
    : false;

  // Memoize lowercase field name to avoid repeated computations
  const variantNameLower = useMemo(
    () => props.params.FieldNames?.toLowerCase(),
    [props.params.FieldNames]
  );

  const articleSettings = useMemo(
    () => siteSettings?.articleSettings as ArticleSettings,
    [siteSettings?.articleSettings]
  );

  const hideAuthorBySignaturePrefix =
    articleSettings?.fields?.hideAuthorBySignaturePrefix?.value === true;

  // Get ActionToolbar settings
  const heroActionToolbarButtons = useMemo(() => {
    if (articleSettings?.fields?.heroActionToolBarButtons) {
      return getActionToolbarButtons(articleSettings.fields.heroActionToolBarButtons);
    }
    return undefined;
  }, [articleSettings?.fields?.heroActionToolBarButtons]);

  const heroActionToolbarIdentifier = useMemo(() => {
    return articleSettings?.fields?.heroEventPosition?.value;
  }, [articleSettings?.fields?.heroEventPosition]);

  // Memoize author data retrieval
  const authorData = useMemo(() => {
    const primaryAuthor = (route.fields?.authorSelection as Data.Contributor.AuthorContributor)
      ?.fields;
    if (primaryAuthor || variantNameLower !== 'alternate') {
      return primaryAuthor;
    }

    const articleSettings = siteSettings?.articleSettings as ArticleSettings;

    return articleSettings?.fields?.defaultAuthor?.fields;
  }, [route.fields?.authorSelection, variantNameLower, siteSettings?.articleSettings]);

  // Convert LinkField to ImageField format for external image fallback
  const externalImageAsImageField = route.fields?.externalPageImage?.value?.href
    ? {
        value: {
          src: route.fields.externalPageImage.value.href,
          alt: route.fields.externalImageAlternateText?.value || '',
        },
      }
    : undefined;

  // Determine which image to use - primary or fallback
  // external image will take precedence over the content page image
  const imageField = route.fields?.externalPageImage?.value?.href
    ? externalImageAsImageField
    : route.fields?.contentPageImage;
  const contentHeadline = usePlainTextWrapper({ field: route?.fields?.contentHeadline });
  const contentDescription = useRichTextWrapper({ field: route.fields?.contentDescription });
  const contentEyebrow = usePlainTextWrapper({ field: route.fields?.contentEyebrow });
  const contentItem = sitecoreContext?.sitecoreContext?.contentItem;
  // Fetching PublishDate, DisplayDate and SEOModifiedDate from content-service
  const PublishDate = contentItem?.PublishDate;
  const SEOModifiedDate = contentItem?.SEOModifiedDate;
  const DisplayDate = contentItem?.DisplayDate;
  const contentPageImage = useImageWrapper({
    priority: true,
    field: imageField,
    className: styles.image,
  });
  const cta1 = useLinkWrapper({
    field: route.fields?.contentPrimaryCta,
    className: 'hero-cta-1',
    ctaComponentClass:
      `component-hero-color-${variantNameLower || 'general'}-cta-1-color` as CtaComponentClass,
    ctaStyle: 'filled',
    contentResultAnalytics: {
      contentListName: route.fields?.contentHeadline?.value?.trim() || undefined,
    },
  });
  const cta2 = useLinkWrapper({
    field: route.fields?.contentSecondaryCta,
    className: 'hero-cta-2',
    ctaComponentClass:
      `component-hero-color-${variantNameLower || 'general'}-cta-2-color` as CtaComponentClass,
    ctaStyle: 'outline',
    contentResultAnalytics: {
      contentListName: route.fields?.contentHeadline?.value?.trim() || undefined,
    },
  });
  //used only in edit mode(page builder) to show the external image as a cta
  const externalImageCTA = useLinkWrapper({
    field: route.fields?.externalPageImage,
    ctaComponentClass:
      `component-hero-color-${variantNameLower || 'general'}-cta-1-color` as CtaComponentClass,
    ctaStyle: 'filled',
    contentResultAnalytics: {
      contentListName: route.fields?.contentHeadline?.value?.trim() || undefined,
    },
  });
  const editingHelpText = useMemo(() => {
    return (
      <EditingHelpText>
        External image as CTA - External image will take precedence over the content page image
      </EditingHelpText>
    );
  }, []);

  return (
    <>
      <Hero
        theme={props.params?.theme as Themes}
        heroVariant={props.params.FieldNames}
        contentHeadline={contentHeadline}
        contentDescription={contentDescription}
        contentEyebrow={contentEyebrow}
        authorName={authorData?.authorName?.value || ''}
        authorProfileURL={authorData?.authorProfileURL?.value?.href || ''}
        hideAuthorBySignaturePrefix={hideAuthorBySignaturePrefix}
        PublishDate={PublishDate}
        DisplayDate={DisplayDate}
        SEOModifiedDate={SEOModifiedDate}
        contentPageImage={contentPageImage}
        cta1={cta1}
        cta2={cta2}
        contentPosition={props.params.Styles as HeroCardAlignment}
        isImagePresent={!!imageField?.value?.src}
        externalPageImage={isEditing ? externalImageCTA : undefined}
        editingHelpText={editingHelpText}
        heroActionToolbarButtons={heroActionToolbarButtons}
        heroActionToolbarIdentifier={heroActionToolbarIdentifier}
        disableHeroActionToolbar={disableHeroActionToolbar}
        isArticlePage={isArticlePage}
      />
    </>
  );
};

export const Default = HeroDynamic;
export const General = HeroDynamic;
export const Alternate = HeroDynamic;
export const Simple = HeroDynamic;
export const Article = HeroDynamic;
